<template>
  <article data-page-name="guide">
    <div class="wrapper">
      <div class="intro">
        <h1>{{ title }}</h1>

        <div v-if="authors.length" class="authors">
          <div class="avatars">
            <AppAvatar
              v-for="author of authors"
              :key="get(author, 'id')"
              :src="get(author, 'data.data.avatar')"
              :alt="get(author, 'data.name', '')"
              :initials="getInitials(get(author, 'data.name', ''))"
            />
          </div>

          <div class="names">
            <template
              v-for="(author, index) of authors"
              :key="get(author, 'id', index)"
            >
              <NuxtLink
                v-if="isString(get(author, 'data.name'))"
                :to="
                  isString(get(author, 'slug'))
                    ? localePathWithSlash(
                        `${PATHS.AUTHORS.path}/${author!.slug}`,
                      )
                    : undefined
                "
                rel="author"
                target="_blank"
              >
                {{ author!.data!.name }}
              </NuxtLink>

              <span v-if="authors[index + 1]"> &nbsp;&&nbsp; </span>
            </template>
          </div>
        </div>
      </div>

      <div class="content">
        <div class="left">
          <ClientOnly>
            <TableOfContent
              v-if="tableOfContent.length"
              :items="tableOfContent"
            >
              <template #title>
                <strong>{{ $t('Table_of_Contents') }}</strong>
              </template>

              <template #item="{ item, active }">
                <NuxtLink :to="item.href" :class="{ active }">
                  <span v-html="item.title" />
                </NuxtLink>
              </template>
            </TableOfContent>
          </ClientOnly>
        </div>

        <div class="middle">
          <div v-html="content" class="markup" />

          <nav class="pagination" aria-label="Article navigation">
            <ul>
              <li>
                <NuxtLink
                  rel="prev"
                  v-bind="
                    prevArticleLink
                      ? { to: prevArticleLink }
                      : { class: 'disabled' }
                  "
                >
                  <SvgIcon name="arrow-backward" />
                  {{ $t('Previous_Article') }}
                </NuxtLink>
              </li>

              <li>
                <NuxtLink
                  rel="next"
                  v-bind="
                    nextArticleLink
                      ? { to: nextArticleLink }
                      : { class: 'disabled' }
                  "
                >
                  {{ $t('Next_Article') }}
                  <SvgIcon name="arrow-forward" />
                </NuxtLink>
              </li>
            </ul>
          </nav>

          <AppSubscribe />
        </div>

        <div class="right">
          <aside>
            <strong>{{ $t('Related_Guides') }}</strong>

            <nav>
              <ul>
                <li
                  v-for="relatedGuide of relatedGuides"
                  :key="get(relatedGuide, 'id')"
                >
                  <NuxtLink
                    v-if="isString(get(relatedGuide, 'data.title'))"
                    :to="resolveRelatedGuideLink(relatedGuide)"
                    :class="{
                      active: postId === relatedGuide.id,
                    }"
                  >
                    {{ relatedGuide!.data!.title }}
                  </NuxtLink>
                </li>
              </ul>
            </nav>
          </aside>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { createError } from '#app';

// components
import AppSubscribe from '~/components/AppSubscribe.vue';
import AppAvatar from '~/components/redesign/AppAvatar.vue';
import TableOfContent from '~/components/redesign/TableOfContent.vue';

// constants
import { PostStatus, PostType } from '~/types';
import { PATHS, STATUS_CODES } from '~/constants';

// composable
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// utils
import get from 'lodash.get';
import { isObject, getInitials, isString } from '~/utils';

const {
  params: { category, slug },
  query: { preview },
} = useRoute();

const localePathWithSlash = useLocalePathWithSlash();

const { data: post } = await useAsyncGql({
  operation: 'BlogArticle',
  variables: {
    slug: slug as string,
    status: preview ? undefined : PostStatus.PUBLISHED,
  },
  options: {
    transform: response => get(response, 'blog_article'),
  },
});

const { data: previewsData } = await useAsyncGql({
  operation: 'BlogArticlesPreviewsFiltered',
  variables: {
    types: PostType.GUIDE,
    category_slugs: [category],
  },
});

if (!post.value) {
  throw createError({
    statusCode: STATUS_CODES.NOT_FOUND,
  });
}

const postId = computed(() => get(post.value, 'id'));
const title = computed(() => get(post.value, 'data.title'));
const authors = computed(() => get(post.value, 'authors', []));
const content = computed(() => get(post.value, 'data.content'));
const relatedGuides = computed(() =>
  get(previewsData.value, 'blog_articles.data', []),
);
const tableOfContent = computed(
  () => get(post.value, 'data.content_table') || [],
);

const resolveRelatedGuideLink = (
  preview: (typeof relatedGuides.value)[number],
): string | undefined => {
  const slug = get(preview, 'slug');
  const categorySlug = get(preview, 'categories[0].slug');

  if ([slug, categorySlug].every(isString)) {
    return localePathWithSlash(`${PATHS.GUIDES.path}${categorySlug}/${slug}`);
  }
};

const prevArticleLink = computed(() => {
  let link = null;

  const currentPostIndexInPreview = relatedGuides.value.findIndex(
    item => item.id === postId.value,
  );

  const prevPreview = relatedGuides.value[currentPostIndexInPreview - 1];

  if (isObject(prevPreview)) {
    link = resolveRelatedGuideLink(prevPreview);
  }

  return link;
});

const nextArticleLink = computed(() => {
  let link = null;

  const currentPostIndexInPreview = relatedGuides.value.findIndex(
    item => item.id === postId.value,
  );

  const nextPreview = relatedGuides.value[currentPostIndexInPreview + 1];

  if (isObject(nextPreview)) {
    link = resolveRelatedGuideLink(nextPreview);
  }

  return link;
});

useSeoMeta({
  title: get(post.value, 'data.seo.title'),
  ogTitle: get(post.value, 'data.seo.title'),
  description: get(post.value, 'data.seo.description'),
  ogDescription: get(post.value, 'data.seo.description'),
  ogImage: get(post.value, 'data.seo.image'),
  ogType: get(post.value, 'data.seo.og_type'),
});
</script>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/typo';
@use '_/mixins/media';
@use '_/mixins/size';
@use '_/variables/font';
@use '_/variables/color';
@use '_/variables/shadow';
@use '_/fn';

[data-page-name='guide'] {
  font-family: font.$poppins;
  padding: 5rem 0;

  @include media.mobile {
    padding: 2.5rem 0;
  }

  .wrapper {
    padding: 0 2rem;

    @include size.wrapper(80rem);

    @include media.mobile {
      padding: 0 1rem;
    }

    .intro {
      margin-bottom: 5rem;

      @include media.custom(900px) {
        margin-bottom: 2.5rem;
      }

      h1 {
        color: fn.token('text-action');

        @include typo.h2;

        text-align: center;
        margin-bottom: 2rem;

        @include media.custom(900px) {
          text-align: left;
        }
      }

      .authors {
        @include flex.center-center;

        gap: 0 2rem;

        @include media.custom(900px) {
          width: max-content;
        }

        .avatars {
          @include flex.center-start;

          [data-component-name='AppAvatar'] {
            &:not(:first-of-type) {
              margin-left: -20px;
            }
          }
        }

        .names {
          font-size: 1.25rem;
          line-height: 1.875rem;
          font-weight: bold;

          a {
            transition: color 0.15s ease;

            &:hover {
              color: #662e9c;
              text-decoration: underline;
            }
          }
        }
      }
    }

    .content {
      @include flex.stretch-sb;

      gap: 0 2rem;

      @include media.custom(900px) {
        flex-direction: column;
      }

      .left,
      .right {
        @include size.fixed-width(17.5rem);

        @include media.custom(900px) {
          @include size.fixed-width(100%);
        }

        > * {
          position: sticky;
          top: calc(var(--scroll-offset) + 1rem);
        }
      }

      .left {
        [data-component-name='TableOfContent'] {
          @include media.custom(900px) {
            margin-bottom: 2.5rem;
          }

          strong {
            @include typo.subtitle-2;

            display: block;
            margin-bottom: 1rem;
          }

          a {
            transition: background-color 0.15s ease;
            display: block;
            padding: 0.5rem 1rem;

            &:not(:has(strong)),
            &:has(strong):deep(strong) {
              color: fn.token('text-secondary');

              @include typo.body-3;
            }

            &.active {
              &:not(:has(strong)),
              &:has(strong):deep(strong) {
                color: fn.token('text-primary');

                @include typo.subtitle-3;
              }
            }

            &:hover {
              background-color: rgb(fn.token('accent-hover'), 16%);
            }
          }
        }
      }

      .right {
        aside {
          strong {
            font-size: 1.25rem;
            line-height: 1.75rem;
            display: block;
            margin-bottom: 0.5rem;
          }

          nav {
            margin-bottom: 1rem;

            @include media.custom(900px) {
              margin-bottom: 5rem;
            }

            ul {
              li {
                border-bottom: 1px solid #e9e9ea;

                a {
                  display: block;
                  padding: 1rem 0;

                  @include typo.body-3;

                  transition: color 0.15s ease;

                  &:hover,
                  &.active {
                    color: #662e9c;
                  }

                  &.active {
                    @include typo.subtitle-3;
                  }
                }
              }
            }
          }

          .purple {
            padding: 2rem 1.5rem;
            background-color: fn.token('surf-cont-tert-action');

            strong {
              text-align: center;
              color: fn.token('text-reversed');
              display: block;
              margin-bottom: 2rem;
            }

            [data-component-name='Buttons.Link'] {
              text-align: center;
            }
          }
        }
      }

      .middle {
        @include size.fluid-max-width(37rem);

        @include media.custom(900px) {
          @include size.fluid-max-width(100%);

          margin-bottom: 5rem;
        }

        &:deep(.markup) {
          font-size: 0.9375rem;
          font-weight: 400;
          line-height: 1.6875rem;
          margin-bottom: 2.5rem;

          a {
            color: fn.token('link');
            text-decoration: underline;
            transition: background-color 0.15s ease;

            &:hover {
              background-color: rgb(fn.token('link'), 16%);
            }
          }

          > p {
            margin: 0.5rem 0;

            &:first-child {
              margin-top: 0;
            }

            &:last-child {
              margin-bottom: 0;
            }
          }

          h2 {
            font-weight: bold;
            font-size: 1.75rem;
            line-height: 2.25rem;
            margin-top: 1.875rem;
          }

          ul {
            list-style: disc;
            padding-left: 1rem;
          }

          img {
            width: 100%;
            height: auto;
          }

          .image-style-align-center {
            margin: 0 auto;
          }

          .image-style-block-align-left {
            margin-right: auto;
          }

          .image-style-block-align-right {
            margin-left: auto;
          }

          figure.table {
            width: 100%;
            margin-left: 0;
            margin-right: 0;

            table {
              border-spacing: 0;
              border: 0;
              max-height: max-content;
              width: 100%;
              height: auto;
              font-family: font.$inter;
              font-size: 1.125rem;
            }

            th,
            td {
              box-sizing: border-box;
              padding: 1rem 0.5rem;
            }

            thead {
              color: fn.token('text-primary');

              @include typo.subtitle-3;

              tr {
                th {
                  border-bottom: 2px solid fn.token('outline-primary');
                }
              }
            }

            tbody {
              th,
              td {
                @include typo.body-3;

                border-bottom: 1px solid fn.token('outline-secondary');
              }
            }
          }
        }

        nav.pagination {
          margin-bottom: 5rem;

          @include media.mobile {
            margin-bottom: 3rem;
          }

          ul {
            @include flex.center-sb;

            li {
              a {
                @include flex.center-center;

                gap: 0 0.25rem;
                color: #662e9c;

                @include typo.subtitle-2;

                &:not(.disabled):hover {
                  text-decoration: underline;
                }

                svg {
                  fill: #662e9c;
                }

                &.disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
